// Heatmap.tsx
import React, { useEffect } from "react";
import { Marker, Popup } from "react-leaflet";
import * as L from "leaflet";
import CustomMapLayer, { HeatPoint } from "./CustomMapLayer";
import "../shared/Heatmap.css";
import { renderToString } from "react-dom/server";
import { MapPin } from "lucide-react";
import type { AreaModel } from "../../../types/Area";
import {
  DEFAULT_ORGANIZATION_MAP_CONFIG,
  type OrganizationMapConfig,
} from "../../../commons/Constant";
import MapGeoJsonBoundaryLayer from "../../../../../components/customs/map/MapGeoJsonBoundaryLayer";
import MapViewportController from "../../../../../components/customs/map/MapViewportController";
import MapPopupController from "../../../../../components/customs/map/MapPopupController";
import MapSurface from "../../../../../components/customs/map/MapSurface";

const circleIcon = (adoptionRate: number) => {
  // const isDarkMode = document.documentElement.classList.contains('dark');

  return L.divIcon({
    html: renderToString(
      <MapPin
        size={20}
        stroke="white"
        fill="#00c29b"
      />,
    ),
    className: "", // remove default class to avoid styling issues
    iconSize: [24, 24],
    iconAnchor: [12, 12], // set anchor to center
    popupAnchor: [0, -12], // popup appears above
  });
};

type PrimaryMapProps = {
  areas: AreaModel[] | null;
  selectedDistrict?: AreaModel | null;
  filterRevision?: number;
  mapConfig?: OrganizationMapConfig;
  onSelectDistrict?: (location: AreaModel | null) => void;
  onResetView?: () => void;
};

const HeatMap: React.FC<PrimaryMapProps> = ({
  areas: heatMap,
  selectedDistrict,
  filterRevision,
  mapConfig = DEFAULT_ORGANIZATION_MAP_CONFIG,
  onSelectDistrict,
  onResetView,
}) => {
  const [greenPoints, setGreenPoints] = React.useState<HeatPoint[]>([]);
  const [orangePoints, setOrangePoints] = React.useState<HeatPoint[]>([]);
  const [redPoints, setRedPoints] = React.useState<HeatPoint[]>([]);

  useEffect(() => {
    if (!heatMap) return;

    setGreenPoints(
      heatMap
        .filter((d) => d.adoption_rate > 70)
        .map((d) => ({
          lat: d.latitude,
          lng: d.longitude,
          intensity: d.adoption_rate / 100,
        })),
    );
    setOrangePoints(
      heatMap
        .filter((d) => d.adoption_rate >= 50 && d.adoption_rate <= 70)
        .map((d) => ({
          lat: d.latitude,
          lng: d.longitude,
          intensity: d.adoption_rate / 100,
        })),
    );
    setRedPoints(
      heatMap
        .filter((d) => d.adoption_rate < 50)
        .map((d) => ({
          lat: d.latitude,
          lng: d.longitude,
          intensity: d.adoption_rate / 100,
        })),
    );
  }, [heatMap]);

  return (
    <div className="h-full w-full p-4">
      <MapSurface
        initialViewport={{ center: mapConfig.center, zoom: mapConfig.zoom }}
        style={{ height: "100%", width: "100%", borderRadius: "1rem" }}
        minZoom={10} // prevent zooming out too far beyond Saginaw area
        maxZoom={18} // optional: prevent zooming in too close
        className="rounded-2xl overflow-hidden"
      >
        <MapViewportController
          viewport={{ center: mapConfig.center, zoom: mapConfig.zoom }}
          revision={filterRevision}
          transition="set"
        />
        <MapPopupController selection={selectedDistrict} />
        <MapGeoJsonBoundaryLayer data={mapConfig.boundary} />

        {/* Heatmap xanh lá */}
        <CustomMapLayer
          points={greenPoints}
          radius={60}
          blur={30}
          max={0.1}
          gradient={{
            0.4: "lightgreen",
            0.65: "green",
            1.0: "darkgreen",
          }}
        />
        {/* Heatmap cam */}
        <CustomMapLayer
          points={orangePoints}
          radius={60}
          blur={30}
          max={0.1}
          gradient={{
            0.4: "#FFD580", // lightorange
            0.65: "#FFA500", // orange
            1.0: "#FF8C00", // darkorange
          }}
        />
        {/* Heatmap đỏ */}
        <CustomMapLayer
          points={redPoints}
          radius={80}
          blur={50}
          max={0.1}
          gradient={{
            0: "#A90000", // lightred
            0.5: "#A90000", // red
            1.0: "#A90000", // darkred
          }}
        />

        {/* Marker từng điểm */}
        {heatMap?.map((d, idx) => (
          <Marker
            position={[d?.latitude, d?.longitude]}
            key={idx}
            icon={circleIcon(d?.adoption_rate)}
            eventHandlers={{
              click: () => onSelectDistrict?.(d as AreaModel),
              popupclose: () => onSelectDistrict?.(null),
            }}
          >
            <Popup>
              <span className="text-[#00BCD4] dark:text-cyan-400">{d?.area_name}</span>
              <div>
                <span className="text-[#4A5565] dark:text-gray-300">
                  {d?.adoption_rate?.toFixed(2)}% adoption
                </span>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapSurface>
    </div>
  );
};

export default HeatMap;
